import React, { useState } from "react";
import {
  ActivityIndicator,
  SafeAreaView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, useLocalSearchParams } from "expo-router";
import CommonServices from "@/services/commonServices";
import useUserStore from "@/store/user";
import LottieConfetti from "@/components/LottieConfetti";

const CancelFinalScreen = () => {
  const { getUserProfile } = useUserStore();
  const params = useLocalSearchParams<{
    reason: string;
    feedback: string;
  }>();

  const [isApplyingOffer, setIsApplyingOffer] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  const handleGet6MonthsFree = async () => {
    try {
      setIsApplyingOffer(true);
      // Apply the offer (using same endpoint for now - could be different offer)
      const res = await CommonServices.claimCancellationOffer();

      if (res.success) {
        await getUserProfile();
        router.dismissAll();
        router.navigate("/(tabs)/account");
      } else {
        console.error("Apply offer failed");
      }
    } catch (error) {
      console.error("Apply offer error:", error);
    } finally {
      setIsApplyingOffer(false);
    }
  };

  const handleContinueCancellation = async () => {
    try {
      setIsCanceling(true);
      const res = await CommonServices.cancelSubscription({
        reason: params.reason || "Unknown",
        other: params.feedback || undefined,
      });

      if (res.success) {
        await getUserProfile();
        // Navigate to success screen
        router.replace("/account/cancel/success");
      } else {
        console.error("Cancel subscription failed:", res.message);
      }
    } catch (error) {
      console.error("Cancel subscription error:", error);
    } finally {
      setIsCanceling(false);
    }
  };

  const isLoading = isApplyingOffer || isCanceling;

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={styles.title}>
            We&apos;ll gift you our{"\n"}Premium for{" "}
            <Text style={styles.titleHighlight}>free</Text>!
          </Text>
        </View>

        <View style={styles.discountGraphic}>
          <Text style={styles.discountNumber}>6</Text>
        </View>

        <View style={styles.subtitleContainer}>
          <Text style={styles.subtitle}>
            Get 6 months for free,{"\n"}no strings attached
          </Text>
        </View>

        <View style={styles.description}>
          <Text style={styles.descriptionText}>
            Continue your subscription and get 6 months free next year.
            You&apos;ll still be able to edit or cancel your subscription at any
            time.
          </Text>
        </View>
      </View>

      <View style={styles.bottomSection}>
        <ActionButton
          title={isApplyingOffer ? "Applying..." : "Get 6 months free"}
          type="primary"
          onPress={handleGet6MonthsFree}
          disabled={isLoading}
          style={styles.primaryButton}
          icon={
            isApplyingOffer ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : undefined
          }
        />

        <TouchableOpacity
          onPress={handleContinueCancellation}
          disabled={isLoading}
          style={styles.cancelButton}
        >
          {isCanceling ? (
            <ActivityIndicator size="small" color={Colors.primary} />
          ) : (
            <Text style={styles.cancelButtonText}>
              No thanks, continue cancellation
            </Text>
          )}
        </TouchableOpacity>
      </View>
      <LottieConfetti active={true} />
    </SafeAreaView>
  );
};

export default CancelFinalScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 60,
    alignItems: "center",
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 30,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    textAlign: "center",
    lineHeight: 37,
  },
  titleHighlight: {
    color: Colors.primary,
  },
  discountGraphic: {
    justifyContent: "center",
    alignItems: "center",
    height: 200,
    marginBottom: 24,
  },
  discountNumber: {
    fontSize: 180,
    fontFamily: FontFamilies.Inter.Bold,
    color: "#0067BB",
    lineHeight: 200,
    textShadowColor: "rgba(13, 45, 71, 0.3)",
    textShadowOffset: { width: -1, height: 5 },
    textShadowRadius: 5,
  },
  subtitleContainer: {
    marginBottom: 16,
  },
  subtitle: {
    fontSize: 22,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    textAlign: "center",
    lineHeight: 28,
  },
  description: {
    paddingHorizontal: 16,
  },
  descriptionText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
    textAlign: "center",
    lineHeight: 20,
  },
  bottomSection: {
    paddingHorizontal: 16,
    paddingBottom: 34,
    paddingTop: 12,
    backgroundColor: Colors.background,
    gap: 12,
  },
  primaryButton: {
    width: "100%",
  },
  cancelButton: {
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButtonText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: Colors.primary,
    textAlign: "center",
    lineHeight: 23,
  },
});
